// src/stores/usePurchaseStore.js
//
// Manages Imagery Purchase mode state:
//   • which grid is active (us48 | glo_ols)
//   • which blocks are currently selected
//   • the active selection tool (click | box)
//
// Selection is a Map keyed by `${grid}:${id}` so switching grids is
// always unambiguous and a stray event can't cross-contaminate state.

import { create } from "zustand";

const PRICE_PER_SQ_MI = 59; // placeholder — wire monetization later

export const usePurchaseStore = create((set, get) => ({
  /* ---------------- state ---------------- */
  activeGrid: "us48",                  // "us48" | "glo_ols"
  tool: "click",                        // "click" | "box"
  selected: new Map(),                  // key: `${grid}:${id}` → { id, grid, areaSqMi }

  /* ---------------- getters -------------- */
  totals: () => {
    const sel = get().selected;
    let sqMi = 0;
    for (const v of sel.values()) sqMi += v.areaSqMi || 0;
    return {
      blocks: sel.size,
      sqMi,
      price: PRICE_PER_SQ_MI == null ? null : sqMi * PRICE_PER_SQ_MI,
    };
  },

  /* ---------------- actions -------------- */
  setActiveGrid: (grid) => {
    if (grid === get().activeGrid) return;
    // If there's an existing selection, clear it. The confirm is handled
    // in the panel UI — the store itself is always decisive.
    set({ activeGrid: grid, selected: new Map() });
  },

  setTool: (tool) => set({ tool }),

  toggleBlock: (grid, id, uid, areaSqMi) => {
    const next = new Map(get().selected);
    const key = `${grid}:${id}`;
    if (next.has(key)) next.delete(key);
    else next.set(key, { id, uid, grid, areaSqMi: areaSqMi || 0 });
    set({ selected: next });
  },

  addBlocks: (grid, features) => {
    // features: [{ id, uid, areaSqMi }]
    const next = new Map(get().selected);
    for (const f of features) {
      const key = `${grid}:${f.id}`;
      if (!next.has(key)) {
        next.set(key, { id: f.id, uid: f.uid, grid, areaSqMi: f.areaSqMi || 0 });
      }
    }
    set({ selected: next });
  },

  removeBlocks: (grid, ids) => {
    const next = new Map(get().selected);
    for (const id of ids) next.delete(`${grid}:${id}`);
    set({ selected: next });
  },

  clear: () => set({ selected: new Map() }),

  isSelected: (grid, id) => get().selected.has(`${grid}:${id}`),
}));